import { ChevronDown, ChevronRight, EyeOff, ZoomIn } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { useIfcFeatures } from '@/context/IfcFeatureContext';
import { filterGroups, GENERAL_GROUP, groupProperties, type PropertyGroup } from '@/helper/ifc/properties';
import { hideElement } from '@/helper/ifc/tree';

// Stable, so the groups memo below still hits when there is nothing to show.
const NO_PROPERTIES: Record<string, string> = {};

export default function IfcPropertiesTab({ tilesetKey }: { tilesetKey: string }) {
	const { t } = useTranslation();
	const { selection, selectedElement, entries, zoomTo, updateVisibility } = useIfcFeatures();
	const [filter, setFilter] = useState('');

	// The index row when the sidecar describes the element, else the three in-tile columns
	// captured at pick time. A placeholder fills an id gap in the sidecar and has no data.
	const indexed = selectedElement && !selectedElement.placeholder ? selectedElement : null;
	const properties = indexed?.properties ?? selection?.summary ?? NO_PROPERTIES;
	const groups = useMemo(
		() => filterGroups(groupProperties(properties), filter),
		[properties, filter],
	);

	if (!selection || selection.key !== tilesetKey) {
		return <p className="p-4 text-xs text-muted-foreground">{t('viewer.ifc.properties.selectElement')}</p>;
	}

	const entry = entries[selection.key];
	const title = properties.Name || properties.IfcClass || `#${selection.featureId}`;
	const subtitle = [properties.IfcClass, properties.GlobalId].filter(Boolean).join(' · ');

	return (
		<div className="flex flex-col gap-3 p-3">
			<div className="min-w-0">
				<div className="text-sm font-semibold truncate" title={title}>
					{title}
				</div>
				<div className="text-xs text-muted-foreground font-mono truncate" title={subtitle}>
					{subtitle}
				</div>
				{entry?.status === 'loading' && (
					<div className="text-xs text-muted-foreground animate-pulse mt-1">{t('viewer.ifc.properties.loading')}</div>
				)}
				{entry?.status === 'error' && (
					<div className="text-xs text-warning mt-1">{t('viewer.ifc.properties.unavailable')}</div>
				)}
			</div>

			<Input
				value={filter}
				onChange={(e) => setFilter(e.target.value)}
				placeholder={t('viewer.ifc.properties.filterPlaceholder')}
				className="h-8 text-xs"
			/>

			<div className="space-y-2">
				{groups.map((group) => (
					<PropertyGroupView
						key={`${selection.key}:${selection.featureId}:${group.name}`}
						group={group}
					/>
				))}
				{groups.length === 0 && (
					<p className="text-xs text-muted-foreground">{t('viewer.ifc.properties.noMatches')}</p>
				)}
			</div>

			<div className="flex gap-2 pt-1">
				<Button
					size="sm"
					variant="outline"
					className="text-xs"
					onClick={() => zoomTo(selection.key, indexed?.bbox ?? null)}
				>
					<ZoomIn className="h-3.5 w-3.5 mr-1" /> {t('viewer.ifc.zoomTo')}
				</Button>
				<Button
					size="sm"
					variant="outline"
					className="text-xs"
					disabled={!indexed}
					title={indexed ? t('viewer.ifc.properties.hideElement') : t('viewer.ifc.properties.needsStructure')}
					onClick={() =>
						updateVisibility(selection.key, (state) => hideElement(state, selection.featureId))
					}
				>
					<EyeOff className="h-3.5 w-3.5 mr-1" /> {t('viewer.ifc.properties.hide')}
				</Button>
			</div>
		</div>
	);
}

function PropertyGroupView({ group }: { group: PropertyGroup }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(true);
	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger
				className={`flex w-full items-center gap-1 rounded px-1 py-1 text-left text-xs
					font-semibold hover:bg-muted/50`}
			>
				{open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
				<span className="truncate">
					{group.name === GENERAL_GROUP ? t('viewer.ifc.properties.general') : group.name}
				</span>
				<span className="ml-auto text-muted-foreground font-normal">{group.rows.length}</span>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<dl
					className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-2 gap-y-0.5 px-1 pb-1 text-xs"
				>
					{group.rows.map((row) => (
						<div key={row.key} className="contents">
							<dt className="truncate text-muted-foreground" title={row.label}>
								{row.label}
							</dt>
							<dd className="break-words" title={row.value}>
								{row.value === '' ? '—' : row.value}
							</dd>
						</div>
					))}
				</dl>
			</CollapsibleContent>
		</Collapsible>
	);
}
